import dayjs from 'dayjs';
import {
  AttentionIcon,
  Bin,
  Chip,
  ColourVariant,
  InfoCard,
  InfoCardDetail,
  IOption,
  ViewIcon,
  LabelColour,
  LinkButton,
  Refresh,
  Tooltip,
  Typography,
} from '@weareopenr/component-library';
import { ScheduleStatus, ValidationStatus } from 'src/api/getSchedules/types';
import type { ScheduleEventType } from 'src/api/getSchedules/types';

import { formatDateTimeDayJs } from 'src/helpers/formatDateTime/formatDateTimeDayJs';
import styles from './ScheduleCard.module.scss';
import { EVENT_TYPES } from '../../api/getEvents';

export type ScheduleCardStatus = ValidationStatus | ScheduleStatus;

const STATUS_CHIP_CONFIG: Record<ScheduleCardStatus, { label: string; colour: ColourVariant }> = {
  NOT_VALIDATED: { label: 'Unvalidated', colour: 'liquorice' },
  VALIDATING: { label: 'Validating', colour: 'honey' },
  VALIDATED: { label: 'Validated', colour: 'kale' },
  VALIDATION_FAILED: { label: 'Caution', colour: 'tomato' },
  SCHEDULED: { label: 'Scheduled', colour: 'liquorice' },
  PENDING: { label: 'Publish in progress', colour: 'honey' },
  SUCCESS: { label: 'Success', colour: 'kale' },
  PARTIAL_SUCCESS: { label: 'Partial success', colour: 'honey' },
  FAILURE: { label: 'Failure', colour: 'tomato' },
  REJECTED: { label: 'Rejected', colour: 'tomato' },
  CANCELLED: { label: 'Cancelled', colour: 'liquorice' },
  SENT: { label: 'Sent', colour: 'liquorice' },
  CAUTION: { label: 'Caution', colour: 'tomato' },
};

const CAUTION_COPY: Partial<Record<ScheduleCardStatus, string>> = {
  [ScheduleStatus.CAUTION]:
    "Products have changed since this menu schedule was created. We'd recommend recreating your schedule and removing this version.",
  [ValidationStatus.VALIDATION_FAILED]:
    'This schedule has some validation issues that need attention. Once fixed, recreate the schedule and remove this version.',
};

const VARIANT_CONFIG: Partial<Record<ScheduleEventType, { label: string; labelColour: LabelColour }>> = {
  PUBLISH_MENU: { label: 'Menu publish', labelColour: 'blue' },
  PUBLISH_PRODUCT: { label: 'Product publish', labelColour: 'purple' },
  PUBLISH_PRICING: { label: 'Pricing publish', labelColour: 'green' },
};

export interface ScheduleCardActions {
  onViewStatus?: () => void;
  onViewScheduleDetails?: () => void;
  onRunValidation?: () => void;
  onViewReport?: () => void;
  onDelete?: () => void;
}

export interface ScheduleCardProps {
  type: ScheduleEventType;
  name: string;
  scheduledTime: string;
  status: ScheduleCardStatus;
  validationTime?: string;
  dataSnapshotDate?: string;
  details: InfoCardDetail[];
  actions?: ScheduleCardActions;
  isHistoric?: boolean;
  // Whether "View validation report" should be enabled. Defaults to the
  // legacy rule (only once validation has failed) so existing callers don't
  // need to change - pass this explicitly for a validation flow that gates
  // report availability some other way (e.g. by whether a report exists yet,
  // rather than by a pass/fail status).
  isReportAvailable?: boolean;
}

export const ScheduleCard = ({
  type,
  name,
  scheduledTime,
  status,
  validationTime,
  dataSnapshotDate,
  details,
  actions,
  isHistoric,
  isReportAvailable = status === ValidationStatus.VALIDATION_FAILED,
}: ScheduleCardProps) => {
  const { label, labelColour } = VARIANT_CONFIG[type] ?? { label: 'Publish', labelColour: 'liquorice' };

  const isMenuPublish = type === EVENT_TYPES.PUBLISH_MENU;

  const validationChipInfo =
    !isMenuPublish && validationTime && status !== ValidationStatus.VALIDATING
      ? `last validated on ${dayjs(validationTime).format('DD/MM/YY [at] HH:mm')}`
      : undefined;

  const chipInfo = isHistoric ? 'Publish Status - ' : validationChipInfo;

  const statusChip = (
    <Chip label={STATUS_CHIP_CONFIG[status].label} backgroundColour={STATUS_CHIP_CONFIG[status].colour} />
  );
  const cautionCopy = CAUTION_COPY[status];
  const statusChipWithTooltip = cautionCopy ? (
    <Tooltip text={cautionCopy} position="top">
      {statusChip}
    </Tooltip>
  ) : (
    statusChip
  );

  const chip =
    !isHistoric && isMenuPublish && dataSnapshotDate ? (
      <>
        <Typography size="small" className={styles['data-snapshot']}>
          Data snapshot from {formatDateTimeDayJs(new Date(dataSnapshotDate))}
        </Typography>
        {statusChipWithTooltip}
      </>
    ) : (
      statusChipWithTooltip
    );

  const ellipsisOptions: IOption[] = [
    {
      id: 'view-schedule-details',
      name: 'View schedule details',
      icon: ViewIcon,
      onOptionClick: actions?.onViewScheduleDetails,
    },
    { id: '_divider_top', isDivider: true },
    // Some validation flows (e.g. menu) run automatically on schedule
    // creation and don't support a manual re-run from here, so the caller
    // simply won't pass a handler for those.
    ...(actions?.onRunValidation
      ? [
          {
            id: 'run-validation',
            name: 'Re-run data validation',
            icon: Refresh,
            isDisabled: status === ValidationStatus.VALIDATING,
            onOptionClick: actions.onRunValidation,
          },
        ]
      : []),
    {
      id: 'view-error-report',
      name: 'View validation report',
      icon: AttentionIcon,
      isDisabled: !isReportAvailable,
      onOptionClick: actions?.onViewReport,
    },
    { id: '_divider', isDivider: true },
    { id: 'delete', name: 'Delete', onOptionClick: actions?.onDelete, icon: Bin, hoverColour: 'tomato' },
  ];

  return (
    <InfoCard
      label={label}
      labelColour={labelColour}
      title={name}
      details={details}
      chipInfo={chipInfo}
      chip={chip}
      ellipsisOptions={isHistoric ? undefined : ellipsisOptions}
      footnote={dayjs(scheduledTime).format('ddd D MMM - HH:mm')}
      action={
        isHistoric && actions?.onViewStatus ? (
          <LinkButton onClick={actions.onViewStatus}>View status</LinkButton>
        ) : undefined
      }
    />
  );
};

export default ScheduleCard;
